export const isValidSSN = (v) => /^\d{3}-\d{2}-\d{4}$/.test(v);

export const formatSSN = (raw) => {
  const digits = raw.replace(/\D/g, '').slice(0, 9);
  if (digits.length <= 3) return digits;
  if (digits.length <= 5) return `${digits.slice(0,3)}-${digits.slice(3)}`;
  return `${digits.slice(0,3)}-${digits.slice(3,5)}-${digits.slice(5)}`;
};

export const isValidPhone = (v) => {
  if (!v) return true; // optional unless at least one required
  return /^[\d\s\-().+]{10,}$/.test(v);
};

export const isValidEmail = (v) => {
  if (!v) return true;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
};

export const isValidZip = (v) => /^\d{5}(-\d{4})?$/.test(v);

export const isValidName = (v) => /^[a-zA-Z\-' ]+$/.test(v.trim());

export const getAge = (dobStr) => {
  if (!dobStr) return 0;
  const dob = new Date(dobStr);
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const m = today.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
  return age;
};

export const isFutureDate = (dateStr) => {
  if (!dateStr) return false;
  return new Date(dateStr) > new Date();
};

export const isWithin90Days = (dateStr) => {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() + 90);
  return d <= cutoff && d >= new Date();
};

export const monthsDiff = (startStr, endStr) => {
  const start = new Date(startStr + '-01');
  const end = new Date(endStr + '-01');
  return (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
};

export const currentMonthYear = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

export const yearsAgoMonthYear = (years) => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - years);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

export const formatMonthYear = (val) => {
  // val is YYYY-MM
  if (!val) return '';
  const [y, m] = val.split('-');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${months[parseInt(m,10)-1]} ${y}`;
};

// Required years of work history. Federal rule is 10 years, but a driver
// only owes history back to age 18 (i.e. back to when they left school).
// A 30-year-old owes 10 years; a 24-year-old owes 6; an 18-year-old owes 0.
export const requiredHistoryYears = (dobStr) => {
  const age = getAge(dobStr);
  if (!age) return 10; // no DOB yet — default to full requirement
  return Math.min(10, Math.max(0, age - 18));
};

// Age (whole years) as of a YYYY-MM month. Month granularity throughout --
// the form never collects a day for employment dates, deliberately, because
// exact days a decade back are invented, not remembered.
export const ageAtMonth = (dobStr, monthYearStr) => {
  if (!dobStr || !monthYearStr) return null;
  const [dy, dm] = dobStr.split('-').map(Number);
  const [ay, am] = monthYearStr.split('-').map(Number);
  if (!dy || !dm || !ay || !am) return null;
  let age = ay - dy;
  if (am < dm) age -= 1;
  return age;
};

// The oldest a driver can have been when school ENDED and still have school
// legitimately be the START of their working life.
//
// 18 = high school. 19-20 = trade school / CDL school. 22 = bachelor's.
// 24 = bachelor's plus a master's, or a slow four-year. Past that, school is
// no longer the origin of a working life -- it is a period WITHIN one, and a
// period is a dated non-working entry, not a terminator.
//
// This is the one tunable number in the rule. Everything else is derived.
export const SCHOOL_MAX_END_AGE = 24;

// A school entry is a TERMINATOR: it claims "everything before this point was
// school", unbounded backward. That claim is only credible if school ended
// while the driver was still young enough for it to have been the beginning.
//
// schoolEndMonth is the entry's end_date, which the ripple pins to the start
// of the entry above it (or the current month when school is the sole entry).
// The driver never types it -- so a 42-year-old who ticks the box on a blank
// form is implicitly claiming he was in school until this month, and is caught.
//
// Fails closed: no DOB, or no end date, means the terminator is not honoured.
export const schoolTerminatorValid = (dobStr, schoolEndMonth) => {
  const age = ageAtMonth(dobStr, schoolEndMonth);
  if (age === null) return false;
  return age <= SCHOOL_MAX_END_AGE;
};

export const formatDate = (val) => {
  // val is YYYY-MM-DD
  if (!val) return '';
  const [y, m, d] = val.split('-');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${months[parseInt(m,10)-1]} ${parseInt(d,10)}, ${y}`;
};
